import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Typography,
  Button
} from '@material-ui/core';

import { OutlinedTextField } from '../common/fields';
import TopSection from '../common/topSection';
import { enqueueSnackbar } from '../snackbar/snackbarSlice';

import { selectUser, saveUserInfo, clearSaveState } from './userSlice';

import validatePassword from '../auth/passwordValidation';

const UserSettingsPage = props => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { info, saveStatus, saveError } = useSelector(selectUser);
  const [ state, setState ] = useState({
    firstName: info.firstName,
    lastName: info.lastName,
    currentPassword: '',
    newPassword: '',
    newPasswordConfirm: ''
  });
  const [ hasUnsavedChanges, setHasUnsavedChanges ] = useState(false);
  const [ errors, setErrors ] = useState({});

  useEffect(() => {
    if(saveStatus === 'failed') {
      setErrors({
        ...errors,
        ...saveError.fields
      });

      dispatch(enqueueSnackbar({
        message: t(`errors:${saveError.message}`),
        options: {
          variant: 'error'
        }
      }));
      dispatch(clearSaveState());

    } else if(saveStatus === 'succeeded') {
      dispatch(enqueueSnackbar({
        message: t('user:savedSuccessfully'),
        options: {
          variant: 'success'
        }
      }));
      dispatch(clearSaveState());

      // Clear password fields
      setState({
        ...state,
        currentPassword: '',
        newPassword: '',
        newPasswordConfirm: ''
      });

      setHasUnsavedChanges(false);
    }
  }, [saveStatus]);


  const onFieldChange = event => {
    setState({
      ...state,
      [event.target.name]: event.target.value
    });

    // Reset errors on this field
    setErrors({
      ...errors,
      [event.target.name]: null
    });

    setHasUnsavedChanges(true);
  }

  const validate = () => {
    const newErrors = {};

    if(state.newPassword) {
      const newPwValidation = validatePassword(state.newPassword);

      if(newPwValidation.error) {
        newErrors.newPassword = newPwValidation.error;
      }

      if(!state.currentPassword) {
        newErrors.currentPassword = t('fieldIsRequired', {field: t('user:currentPassword')});
      }

      if(state.newPassword !== state.newPasswordConfirm) {
        newErrors.newPasswordConfirm = t('user:newPasswordsMustMatch');
      }
    }

    setErrors(newErrors);

    if(Object.keys(newErrors).every(field => newErrors[field] === null)) {
      return true;
    }

    return false;

  }

  const onSubmit = event => {
    event.preventDefault();

    if(hasUnsavedChanges) {
      if(validate()) {
        dispatch(saveUserInfo(state));
      }
    }
  }

  return (
    <>
      <TopSection>
        <Typography variant="h4" component="h1">{t('user:settings')}</Typography>
      </TopSection>
      <Box p={3.5}>
        <Typography variant="h6" component="h2">{t('user:userSettings')}</Typography>
        <Box my={3}>
          <OutlinedTextField
            label={t('user:username')}
            name="username"
            value={info.username}
            disabled
          />
        </Box>
        <form onSubmit={onSubmit}>
          <Box my={3}>
            <OutlinedTextField
              label={t('user:firstName')}
              name="firstName"
              value={state.firstName}
              onChange={onFieldChange}
              error={!!errors.firstName}
            />
          </Box>
          <Box my={3}>
            <OutlinedTextField
              label={t('user:lastName')}
              name="lastName"
              value={state.lastName}
              onChange={onFieldChange}
              error={!!errors.lastName}
            />
          </Box>
          <Typography variant="h6" component="h2">{t('user:changePassword')}</Typography>
          <Box my={3}>
            <OutlinedTextField
              label={t('user:currentPassword')}
              name="currentPassword"
              value={state.currentPassword}
              placeholder={t('user:currentPasswordPlaceholder')}
              type="password"
              onChange={onFieldChange}
              error={!!errors.currentPassword}
              helperText={errors.currentPassword || ''}
            />
          </Box>
          <Box my={3}>
            <OutlinedTextField
              label={t('user:newPassword')}
              name="newPassword"
              value={state.newPassword}
              placeholder={t('user:newPasswordPlaceholder')}
              type="password"
              onChange={onFieldChange}
              error={!!errors.newPassword}
              helperText={errors.newPassword || ''}
            />
          </Box>
          <Box my={3}>
            <OutlinedTextField
              label={t('user:newPasswordConfirm')}
              name="newPasswordConfirm"
              value={state.newPasswordConfirm}
              placeholder={t('user:newPasswordPlaceholder')}
              type="password"
              onChange={onFieldChange}
              error={!!errors.newPasswordConfirm}
              helperText={errors.newPasswordConfirm || ''}
            />
          </Box>
          <Button variant="contained" color="primary" disableElevation disabled={!hasUnsavedChanges || saveStatus === 'loading'} type="submit">{t('common:saveChanges')}</Button>
        </form>
      </Box>
    </>
  );
};

export default UserSettingsPage;
